import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ValidationErrors, ValidatorFn } from '@angular/forms';
import { LocationService } from '../../../../service/location.service';
import { LossPreventionService } from '../../../../service/loss-prevention.service';
import {
  AssignmentDetails,
  AssignmentTimelineEntry,
  FeatureFlag,
  IncidentFormContext,
  IncidentFormGroup,
  LiveCamera,
  LocationResponse,
  TimelineRow,
  VideoClip,
  VideoSnapshot,
} from '../../../../types';
import { atLeastOneExtraLinkValidator, incidentFormValidator } from '../review-submit-form/form-utils';
import { ReviewFormService } from '../review-submit-form/review-form.service';
import { getLpReviewUrl } from '../review-utils';
import { NotifyService } from 'src/app/service/notify.service';
import { PosthogService } from 'src/app/service/posthog.service';
import { AssignmentDetailsStore } from '../investigation-assignment/assignment-details.store';

@Component({
  selector: 'app-walkout-incident-form',
  templateUrl: './walkout-incident-form.component.html',
  styleUrl: './walkout-incident-form.component.scss',
  standalone: false,
})
export class WalkoutIncidentFormComponent implements AfterViewInit, OnDestroy {
  public reviewFormService = inject(ReviewFormService);
  private lpService = inject(LossPreventionService);
  private locationService = inject(LocationService);
  private notifyService = inject(NotifyService);
  private assignmentDetailsStore = inject(AssignmentDetailsStore);
  private posthogService = inject(PosthogService);
  public imageId = signal<string | undefined>(undefined);
  public location = signal<LocationResponse | undefined>(undefined);
  public liveCameras = signal<LiveCamera[] | undefined>(undefined);
  public videoClips = signal<VideoClip[]>([]);
  public timeline = signal<AssignmentTimelineEntry[]>([]);
  public instructions = signal<string | undefined>(undefined);
  public recentWalkins = signal<VideoSnapshot[]>([]);
  public incidentDateEditable = input<boolean>(false);
  public isLoading = signal<boolean>(false);
  public videoExpanded = signal<boolean>(false);
  public isExtraLinkRequired = signal<boolean>(false);
  public locationTimezone = input<string | undefined>();
  public contextReady = output<IncidentFormContext>();
  public formRefReady = output<TemplateRef<unknown>>();
  public formRef = viewChild<TemplateRef<unknown>>('formBody');

  constructor() {
    this.reviewFormService.form.setValidators(this.walkoutAssignmentValidator);
    this.reviewFormService.form.updateValueAndValidity({ emitEvent: false });
    effect(() => {
      const details = this.assignmentDetailsStore.details();
      if (!details) return;
      this.loadWalkout(details);
    });
  }

  public timelineRows = computed<TimelineRow[]>(() => {
    return this.timeline().map((entry) => ({
      when: entry.occurred,
      timestamp: entry.timestamp,
      items: entry.items?.map((i) => `${i.description} ($${Number(i.total).toFixed(2)})`).join('\n') ?? '',
      highlight: entry.occurred === 'walkout',
      transactionId: entry.platform_id ?? entry.transaction_id ?? null,
    }));
  });

  public showLpCameras = computed(() => this.posthogService.isFeatureEnabledByEnum(FeatureFlag.EnableLpCameraLinks));

  public lpCameraLinks = computed(() => {
    const details = this.assignmentDetailsStore.details();
    if (!details) return [];
    const base = getLpReviewUrl(details.assignment_id);
    return (this.liveCameras() ?? []).map((camera) => ({
      url: `${base}?camera=${encodeURIComponent(camera.provider_camera_id)}`,
    }));
  });

  public ngAfterViewInit(): void {
    this.formRefReady.emit(this.formRef());
  }

  public ngOnDestroy(): void {
    this.reviewFormService.form.setValidators(incidentFormValidator);
  }

  submitNotUseful({ videoClipId, clipNotUsefulReason }) {
    this.lpService.reportWalkout(this.assignmentDetailsStore.details()!.assignment_id, clipNotUsefulReason, videoClipId).subscribe(() => {
      this.notifyService.notify('Clip successfully reported');
      this.videoClips.update((clips) => clips.map((clip) => (clip.id === videoClipId ? { ...clip, not_useful_submitted: true } : clip)));

      if (this.isAllClipsNotUseful()) {
        this.isExtraLinkRequired.set(true);
      }
    });
  }
  public isAllClipsNotUseful() {
    const clips = this.videoClips();

    if (!clips || clips.length === 0) return true;
    return clips.every((clip) => clip.not_useful_submitted === true);
  }

  private loadWalkout(details: AssignmentDetails) {
    if (!details) return;

    const txnDate = details.transaction_details?.transaction?.date;
    const incidentDate = txnDate ? new Date(txnDate) : undefined;
    this.imageId.set(details.subject_id);
    this.liveCameras.set(details.live_streams);
    this.videoClips.set(details.video_clips ?? []);
    this.reviewFormService.form.updateValueAndValidity();
    this.timeline.set(details.transaction_details?.timeline ?? []);
    this.instructions.set(details.instructions);
    this.recentWalkins.set(details.video_snapshots ?? []);
    this.reviewFormService.form.controls.instance_at.setValue(incidentDate);
    this.reviewFormService.caseImages.set(details.case_images);

    this.contextReady.emit({
      locationId: details.location_id,
      instanceAtDate: incidentDate,
      form: this.reviewFormService.form,
    });
    this.isExtraLinkRequired.set(this.isAllClipsNotUseful());
    this.locationService.getLocation({ locationId: details.location_id }).subscribe((location) => this.location.set(location));
  }

  private readonly walkoutAssignmentValidator: ValidatorFn = (form: IncidentFormGroup): ValidationErrors | null => {
    if (!form.controls.isTheft.value) return null;
    const baseErrors = incidentFormValidator(form) || {};
    const noVideoClips = this.videoClips().length === 0;
    const needsExtraLink = noVideoClips ? atLeastOneExtraLinkValidator(form.controls.extraLinks) : null;
    const extraLinkErrors = needsExtraLink ?? {};
    const merged = { ...baseErrors, ...extraLinkErrors } as ValidationErrors;
    return Object.keys(merged).length > 0 ? merged : null;
  };
}
